// Запросы вкладки «Расчёт сотрудника».
//
// Ключ лежит в пространстве «payroll»: пересчёт, утверждение ведомости и отметка выплаты сбрасывают
// его вместе с остальным разделом, и документ не показывает деньги прошлого расчёта.

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { EmployeeOut } from '@/shared/api/endpoints/people';
import { getEmployee, listEmployees } from '@/shared/api/endpoints/people';
import type { PayslipPeriodOut } from '@/shared/api/endpoints/payslip';
import { fetchPayslipPeriod } from '@/shared/api/endpoints/payslip';
import type { PersonMonthHoursOut } from '@/shared/api/endpoints/timesheet';
import { setPersonMonthHours } from '@/shared/api/endpoints/timesheet';
import type { ListResponse } from '@/shared/api/types';
import type { ApiError } from '@/shared/errors/api-error';
import type { Period } from '@/shared/time/period';
import { formatPeriodKey } from '@/shared/time/period';
import { подтверждение } from '@/shared/ui/toast/подтверждение';

import { уволенныеПервыми } from './тексты';

/**
 * Документ «расчёт сотрудника» за выбранные месяцы.
 *
 * `от` и `до` необязательны: не заданы — сервер сам берёт от первой ведомости человека до месяца
 * увольнения либо последнего месяца с ведомостью. Запрос идёт сразу, как только выбран человек:
 * кнопки «Рассчитать» нет, документ строится по самому выбору.
 *
 * Прежний документ остаётся на экране, пока едет следующий, но ТОЛЬКО тот же человека: когда
 * листают месяцы, цифры не мигают скелетом, а когда выбран другой человек, деньги прежнего
 * показывать нельзя ни на секунду — под именем нового они читались бы как его.
 */
export function useРасчётСотрудника(
  employeeId: string | null,
  от: Period | null,
  до: Period | null,
): UseQueryResult<PayslipPeriodOut, ApiError> {
  return useQuery<PayslipPeriodOut, ApiError>({
    queryKey: [
      'payroll',
      'settlement',
      employeeId,
      от === null ? null : formatPeriodKey(от),
      до === null ? null : formatPeriodKey(до),
    ],
    queryFn: ({ signal }) =>
      fetchPayslipPeriod(
        employeeId ?? '',
        {
          year_from: от?.year,
          month_from: от?.month,
          year_to: до?.year,
          month_to: до?.month,
        },
        signal,
      ),
    enabled: employeeId !== null,
    placeholderData: (прежние, прежнийЗапрос) =>
      прежнийЗапрос?.queryKey[2] === employeeId ? прежние : undefined,
  });
}

/** Вынесено в функцию модуля: запрос пересчитывает `select` при каждой новой ссылке на него. */
function разложитьЛюдей(ответ: ListResponse<EmployeeOut>): ListResponse<EmployeeOut> {
  return { ...ответ, items: уволенныеПервыми(ответ.items) };
}

/** Сколько людей показывать в выборе. Больше полусотни в списке не читают. */
const ПОТОЛОК_ВЫБОРА = 50;

/**
 * Люди для выбора расчёта: уволенные первыми.
 *
 * Поиск пуст — показываются только уволенные: расчёт нужен прежде всего тому, кто ушёл или
 * уходит, и искать его среди всех не надо. Есть запрос — ищем среди ВСЕХ состояний, потому что
 * рассчитывать могут и работающего (перед увольнением), а фамилию человек знает.
 */
export function useЛюдиДляРасчёта(
  запрос: string,
): UseQueryResult<ListResponse<EmployeeOut>, ApiError> {
  const q = запрос.trim();
  const params =
    q === '' ? { status: 'fired' as const, limit: ПОТОЛОК_ВЫБОРА } : { q, limit: ПОТОЛОК_ВЫБОРА };
  return useQuery<ListResponse<EmployeeOut>, ApiError>({
    queryKey: ['people', 'lookup', params, 'settlement'],
    queryFn: ({ signal }) => listEmployees(params, signal),
    select: разложитьЛюдей,
    // Список не пропадает, пока едет следующий: человек печатает фамилию по букве, и мигание
    // списка под курсором читается как поломка.
    placeholderData: keepPreviousData,
  });
}

/**
 * Человек по идентификатору: имя в шапке выбора и «вернуться к расчёту».
 *
 * Ключ тот же, что у карточки человека в разделе «Люди»: открытая недавно карточка отдаёт имя
 * из кэша без запроса. Без идентификатора запрос не идёт вовсе.
 */
export function useЧеловек(id: string | null): UseQueryResult<EmployeeOut, ApiError> {
  return useQuery<EmployeeOut, ApiError>({
    queryKey: ['people', 'employee', id],
    queryFn: ({ signal }) => getEmployee(id ?? '', signal),
    enabled: id !== null,
  });
}

/** Что нужно, чтобы внести часы: объект, человек, месяц, часы строкой и ключ операции. */
export interface ВводЧасов {
  siteId: string;
  employeeId: string;
  период: Period;
  /** Часы строкой с точкой: «60», «60.5». Число с плавающей точкой по сети не ходит. */
  часы: string;
  /** Живёт, пока живо содержимое: повтор тех же часов приходит с тем же ключом. */
  ключ: string;
}

/**
 * «Внести часы»: итог за месяц по одному человеку на одном объекте.
 *
 * После успеха сбрасывается всё пространство «payroll»: документ перечитывается и показывает, что
 * часы внесены и ведомость ещё не пересчитана, а сетка табеля и список периодов не остаются со
 * старыми числами. Отдельно сбрасывается «people»: часы человека по месяцам живут и в его карточке.
 *
 * Деньги тут не пересчитываются и не должны: пересчёт ведомости это отдельная кнопка, чтобы
 * документ не менял суммы тихо.
 */
export function useВнестиЧасы(): UseMutationResult<PersonMonthHoursOut, ApiError, ВводЧасов> {
  const client = useQueryClient();
  return useMutation<PersonMonthHoursOut, ApiError, ВводЧасов>({
    mutationFn: ({ siteId, employeeId, период, часы, ключ }) =>
      setPersonMonthHours(siteId, employeeId, период, часы, ключ),
    meta: подтверждение<ВводЧасов, PersonMonthHoursOut>(
      'Часы внесены',
      (_ввод, итог) =>
        итог.warnings[0] ??
        (итог.payroll?.recalc_needed === true ? 'ведомость ещё не пересчитана' : undefined),
    ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['payroll'] });
      void client.invalidateQueries({ queryKey: ['people'] });
    },
  });
}
